import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { UserDTO } from '../../models/users.dto';
import { IdentityFacade } from '../../application-services/identity.facade';
import { webApiUrl } from '@domains/shared/domain/utils/url';
import { headers } from '@domains/shared/domain/utils/global-constants';
import { BackofficeCompanyDetailDTO } from '../../models/company-backoffice-detail.interface';
import { CompanyCourseModel } from '../../models/company-course.model';
import { UserFormModel } from '../../models/user-form.model';
import { CompanyEmailTemplateDTO } from '../../models/company-email-template.interface';
import { EmailTemplateKindEnum } from '../../models/enums/email-template-kind.enum';
import { BackofficeCompanyCourseDTO } from '@domains/course/domain/models/company-course.interface';
import { ReminderResult, reminderResultOf } from '../../models/reminder-result.interface';


@Injectable({ providedIn: 'root' })
export class BackofficeCompanyService {

  private baseUrl = `${webApiUrl}/backoffice/Company`;

  constructor(
    private http: HttpClient,
    private identityFacade: IdentityFacade) {
  }

  updateCompany(company: BackofficeCompanyDetailDTO): Observable<BackofficeCompanyDetailDTO> {
    const url = `${this.baseUrl}/${company.id}`;

    return this.http.put<BackofficeCompanyDetailDTO>(url, company, { headers });
  }

  getCompanyCourses(companyId: string): Observable<BackofficeCompanyCourseDTO[]> {
    const url = `${this.baseUrl}/${companyId}/course`;
    return this.http.get<BackofficeCompanyCourseDTO[]>(url, { headers });
  }

  /**
   * Výchozí znění e-mailu pro každý jazyk firmy. Editor šablon jím po resetu vyplní
   * pole, aby bylo vidět, co se doopravdy odešle, místo prázdna.
   */
  getEmailTemplateDefaults(
    companyId: string,
    kind: EmailTemplateKindEnum,
    url: string = `${this.baseUrl}/${companyId}/emailTemplateDefaults?kind=${kind}`,
  ): Observable<CompanyEmailTemplateDTO[]> {
    return this.http.get<CompanyEmailTemplateDTO[]>(url, { headers });
  }

  /** Nahraje logo firmy. FormData jde bez sdílených headers — boundary si řeší prohlížeč. */
  uploadLogo(companyId: string, file: File, url: string = `${this.baseUrl}/${companyId}/logo`): Observable<string> {
    const formData = new FormData();
    formData.append('model', file);

    return this.http.post<string>(url, formData);
  }

  getUsers(companyId: string): Observable<UserDTO[]> {
    const url = `${this.baseUrl}/${companyId}/user`;
    return this.http.get<UserDTO[]>(url, { headers });
  }



  createUser(companyId: string, user: UserFormModel): Observable<UserDTO> {
    const url = `${this.baseUrl}/${companyId}/user`;
    return this.http.post<UserDTO>(url, user, { headers });
  }

  createUsers(companyId: string, users: UserFormModel[]): Observable<UserDTO[]> {
    const url = `${this.baseUrl}/${companyId}/users`;
    return this.http.post<UserDTO[]>(url, users, { headers });
  }

  getCompany(id: string): Observable<BackofficeCompanyDetailDTO> {
    const url = [this.baseUrl, id].join('/');
    return this.http.get<BackofficeCompanyDetailDTO>(url, { headers });
  }

  //TODO: Do it in companyCourseFacade
  updateEmail(course: BackofficeCompanyCourseDTO) {
    const url = `${webApiUrl}/backoffice/CompanyCourse/${course.id}/Email`;
    return this.http.put<CompanyCourseModel>(url, course, { headers: headers });
  }

  /**
   * Hromadná urgence za celou firmu. Studenti se nevybírají — dostane ji každý,
   * na koho varianta sedí. Vrací počet skutečně odeslaných.
   */
  sendReminder(companyId: string, audience: 'unregistered' | 'courseUnfinished'): Observable<ReminderResult> {
    const url = `${this.baseUrl}/${companyId}/Reminder/${audience}`;

    return this.http.post<number>(url, {}, { headers, observe: 'response' }).pipe(map(reminderResultOf));
  }
}
